/**
 * Safely extract a human-readable error message from any axios / fetch error.
 *
 * Problem this solves:
 *   FastAPI/Pydantic 422 responses return `detail` as an ARRAY of error
 *   objects like `[{type, loc, msg, input, ctx}, ...]`. If we pass that
 *   array directly to `toast.error(...)` or render it in JSX, React explodes
 *   with error #31 ("Objects are not valid as a React child").
 *
 * Usage:
 *   import { errorText } from '@/lib/errorText';
 *   } catch (err) { toast.error(errorText(err, 'Failed to save')); }
 */
export function errorText(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  const detail = err?.response?.data?.detail;

  // Pydantic 422 — array of {type, loc, msg, input, ctx}
  if (Array.isArray(detail)) {
    const parts = detail.map(d => {
      if (typeof d === 'string') return d;
      if (d && typeof d === 'object') {
        const loc = Array.isArray(d.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
        const msg = d.msg || d.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      }
      return String(d);
    });
    return parts.join('; ') || fallback;
  }

  // Plain object with `msg`
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }

  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;
  return fallback;
}
